import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from './Header';
import Footer from './Footer';

const Onboarding = ({ onBack }) => {
  const navigate = useNavigate();
  const { register, loading, error, isAuthenticated, user } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleBackToHome = () => {
    // Clear authentication state
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^(\+260|0)?[97][0-9]{8}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid Zambian phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateStep1()) return;

    try {
      await register(
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.password,
        formData.phone
      );
      // Navigate to OTP verification page with email in state
      // navigate('/verify-otp', { state: { email: formData.email } });
      setShowSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrors(prev => ({ ...prev, submit: err.message || 'Registration failed' }));
    }
  };

  const renderStep1 = () => (
    <div className="space-y-3">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-playfair font-bold text-charcoal mb-3">Create Your Account</h2>
        <p className="text-charcoal/70">Join Wako to find meaningful connections</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-charcoal mb-2">First Name *</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="First Name"
            disabled={loading}
            className={`w-full px-3 py-2 rounded-lg border-2 text-sm ${
              errors.firstName ? 'border-rose' : 'border-charcoal/20'
            } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
          />
          {errors.firstName && <p className="text-rose text-xs mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-charcoal mb-2">Last Name *</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Last Name"
            disabled={loading}
            className={`w-full px-3 py-2 rounded-lg border-2 text-sm ${
              errors.lastName ? 'border-rose' : 'border-charcoal/20'
            } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
          />
          {errors.lastName && <p className="text-rose text-xs mt-1">{errors.lastName}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Email *</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="john@example.com"
          disabled={loading}
          className={`w-full px-3 py-2 rounded-lg border-2 text-sm ${
            errors.email ? 'border-rose' : 'border-charcoal/20'
          } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
        />
        {errors.email && <p className="text-rose text-xs mt-1">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Phone *</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="+260 97 123 4567"
          disabled={loading}
          className={`w-full px-3 py-2 rounded-lg border-2 text-sm ${
            errors.phone ? 'border-rose' : 'border-charcoal/20'
          } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
        />
        {errors.phone && <p className="text-rose text-xs mt-1">{errors.phone}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-charcoal mb-2">Password *</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="••••••"
            disabled={loading}
            className={`w-full px-3 py-2 rounded-lg border-2 text-sm ${
              errors.password ? 'border-rose' : 'border-charcoal/20'
            } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
          />
          {errors.password && <p className="text-rose text-xs mt-1">{errors.password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-charcoal mb-2">Confirm Password *</label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="••••••"
            disabled={loading}
            className={`w-full px-3 py-2 rounded-lg border-2 text-sm ${
              errors.confirmPassword ? 'border-rose' : 'border-charcoal/20'
            } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
          />
          {errors.confirmPassword && <p className="text-rose text-xs mt-1">{errors.confirmPassword}</p>}
        </div>
      </div>
      {/* {errors.submit && (
        <div className="bg-rose/10 border-2 border-rose/50 text-rose rounded-xl p-4">
          {errors.submit}
        </div>
      )} */}

      {error && (
        <div className="bg-rose/10 border-2 border-rose/50 text-rose rounded-xl p-4">
          {error}
        </div>
      )}
      <br />
      <button
        type="submit"
        onClick={handleRegister}
        disabled={loading}
        className="w-full bg-rose text-white py-3 rounded-lg font-semibold hover:bg-rose/70 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Creating Account...' : 'Create Account'}
      </button>

      {/* <p className="text-center text-sm text-charcoal/70">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="text-rose font-semibold hover:underline"
        >
          Log in
        </button>
      </p> */}
    </div>
  );

  return (
    <div className="bg-offwhite text-charcoal antialiased">
      <Header onNavigateToOnboarding={onBack} isOnboarding={true} />
      
      <div className="min-h-screen py-32 px-6 bg-gradient-to-b from-offwhite to-blush">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
           
           {showSuccess ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-6">🎉</div>
                <h2 className="text-2xl font-playfair font-bold text-charcoal mb-4">You're All Set!</h2>
                <p className="text-charcoal/70 mb-8 max-w-md mx-auto">
                  Welcome👋<br/>
                  You're on the early-access list.
                </p>
                <div className="bg-gradient-to-r from-rose/10 to-transparent border-2 border-rose/20 rounded-xl p-6 max-w-md mx-auto mb-8">
                  <p className="text-charcoal/80">
                    <strong className="text-rose">What's Next?</strong><br/>
                    We'll notify you once your profile is activated
                  </p>
                  <br/>
                  <p>
                    Status: <strong className="text-rose">Pending</strong><br/>
                  </p>
                </div>
                {/* <div className="flex justify-center mb-6">
                  <a 
                    href="https://facebook.com/wako" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    title="Follow us on Facebook"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div> */}
                <button
                  onClick={handleBackToHome}
                  className="px-8 py-3 bg-rose text-white rounded-md font-semibold hover:bg-charcoal transition-colors duration-300"
                >
                  Back to Home
                </button>
              </div>
            ) : (
            <form onSubmit={handleRegister}>
              {renderStep1()}
            </form>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Onboarding;
